/**
 * Audit engine.
 *
 * Reads cells from a `WorkbookModel` (typed arrays + interned formula
 * ids); runs every enabled rule (system + user) per sheet. Lazily
 * builds Layer 2 (`ensurePatternAnalysis`) if any runnable rule
 * declares `needsPatternAnalysis`.
 */

import { ensurePatternAnalysis, getWorkbookModel } from "../../model";
import type { Scope } from "../types";
import type {
  AuditCheck,
  AuditFinding,
  AuditFindingPartial,
  AuditRule,
  SystemAuditRule,
  UserAuditRule,
} from "./types";
import { evaluateUserAuditRule } from "./matchEvaluator";

type Reporter = (message: string) => void;

type RunnableSystem = { kind: "system"; rule: SystemAuditRule; check: AuditCheck };
type RunnableUser = { kind: "user"; rule: UserAuditRule };
type Runnable = RunnableSystem | RunnableUser;

export async function runAudit(
  rules: AuditRule[],
  scope: Scope,
  report: Reporter = () => {}
): Promise<AuditFinding[]> {
  const enabled = rules.filter((r) => r.enabled);
  if (enabled.length === 0) {
    report("no enabled audit checks");
    return [];
  }

  const model = await getWorkbookModel(scope);

  if (model.sheets.size === 0) {
    report("no cells in scope");
    return [];
  }

  // Partition rules into runnable system + user.
  const runnable: Runnable[] = [];
  for (const rule of enabled) {
    if (rule.source === "system") {
      runnable.push({ kind: "system", rule, check: rule.check });
    } else {
      runnable.push({ kind: "user", rule });
    }
  }
  if (runnable.length === 0) {
    report("nothing runnable");
    return [];
  }

  // Lazily build Layer 2 if any runnable rule needs it.
  if (
    runnable.some((r) => r.kind === "system" && r.rule.needsPatternAnalysis)
  ) {
    const t0 = performance.now();
    await ensurePatternAnalysis(model);
    report(`pattern analysis: ${(performance.now() - t0).toFixed(0)}ms`);
  }

  const all: AuditFinding[] = [];

  for (const sheet of Array.from(model.sheets.values())) {
    const cellCount = sheet.bounds.rows * sheet.bounds.cols;
    report(`auditing ${sheet.name}: ${cellCount.toLocaleString()} cells`);

    for (const item of runnable) {
      let partials: AuditFindingPartial[];
      try {
        if (item.kind === "system") {
          partials = item.check(sheet, model);
        } else {
          partials = evaluateUserAuditRule(item.rule, sheet, model);
        }
      } catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        report(`${item.rule.name} threw on ${sheet.name}: ${reason}`);
        continue;
      }
      for (const p of partials) {
        all.push({
          ...p,
          ruleId: item.rule.id,
          ruleName: item.rule.name,
          severity: item.rule.severity,
        });
      }
    }
  }

  return all;
}
